import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { 
    getStatusColor, 
    calculateProgress, 
    getCurrentStep, 
    formatDateTime,
    STEP_DETAILS,
    PIPELINE_STATUSES
} from '../../utils/pipelineHelpers';

function PipelineCard({ pipeline, onClick }) {
    const { t } = useTranslation();
    const progress = calculateProgress(pipeline.steps);
    const currentStep = getCurrentStep(pipeline.steps);
    const currentStepInfo = currentStep ? STEP_DETAILS[currentStep.step_name] : null;

    const getStatusBadge = (status) => {
        const colorClasses = getStatusColor(status);
        
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorClasses}`}>
                {t(`PipelineResults.${status}`)}
            </span>
        );
    };

    const isActive = [PIPELINE_STATUSES.PENDING, PIPELINE_STATUSES.PROCESSING].includes(pipeline.status);

    return (
        <div
            data-testid={`pipeline-card-${pipeline.uuid}`}
            onClick={onClick}
            className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all cursor-pointer relative group overflow-hidden"
        >
            <div className="p-8">
                {/* Header Section */}
                <div className="flex justify-between items-start mb-6">
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {pipeline.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                            {pipeline.status === PIPELINE_STATUSES.PROCESSING ? t('ResearchPipeline.pipelineInProgress') :
                             pipeline.status === PIPELINE_STATUSES.PENDING ? t('ResearchPipeline.pipelineStarting') :
                             pipeline.status === PIPELINE_STATUSES.COMPLETED ? t('ResearchPipeline.pipelineCompletedDesc') :
                             pipeline.status === PIPELINE_STATUSES.FAILED ? t('ResearchPipeline.pipelineFailed') :
                             pipeline.status === PIPELINE_STATUSES.CANCELLED ? t('ResearchPipeline.pipelineCancelled') :
                             t('ResearchPipeline.title')}
                        </p>
                    </div>
                    <div className="ml-4">
                        {getStatusBadge(pipeline.status)}
                    </div>
                </div>

                {/* Current Step or Completion Box */}
                {(isActive || pipeline.status === PIPELINE_STATUSES.COMPLETED) && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                        {pipeline.status === PIPELINE_STATUSES.COMPLETED ? (
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t('ResearchPipeline.pipelineCompleted')}
                                </h4>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {t('ResearchPipeline.pipelineCompletedDesc')}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t(`PipelineResults.pipelineSteps.${currentStep.step_name}.name`) || currentStepInfo?.name || t('ResearchPipeline.preparingPipeline')}
                                </h4>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {t(`PipelineResults.pipelineSteps.${currentStep.step_name}.description`) || currentStepInfo?.description || t('ResearchPipeline.preparingPipelineDesc')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Progress Bar Section */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">{t('ResearchPipeline.currentStep')}</span>
                        <span className="text-sm font-bold text-gray-900">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                                pipeline.status === PIPELINE_STATUSES.COMPLETED
                                    ? 'bg-black'
                                    : pipeline.status === PIPELINE_STATUSES.FAILED
                                    ? 'bg-gray-600'
                                    : pipeline.status === PIPELINE_STATUSES.CANCELLED
                                    ? 'bg-gray-500'
                                    : isActive
                                    ? 'bg-gray-800'
                                    : 'bg-gray-400'
                            }`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Project and Description */}
                {pipeline.project && (
                    <div className="mb-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{t('ResearchPipeline.project')}:</span>
                            <span className="text-sm font-medium text-gray-900">
                                {pipeline.project.name}
                            </span>
                            {pipeline.project.color === '#007bff' && pipeline.project.description?.includes('Auto-created') && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                    {t('ResearchPipeline.autoCreated')}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {pipeline.research_description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-6">
                        {pipeline.research_description}
                    </p>
                )}

                {/* Subtle arrow indicator at bottom */}
                <div className="flex justify-end pt-4 border-t border-gray-100">
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-black transition-colors" />
                </div>

                {/* Error Message */}
                {pipeline.status === PIPELINE_STATUSES.FAILED && pipeline.error_message && (
                    <div className="mt-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                        <p className="text-sm font-medium text-gray-800 mb-1">{t('ResearchPipeline.errorOccurred')}</p>
                        <p className="text-sm text-gray-600 line-clamp-2">{pipeline.error_message}</p>
                    </div>
                )}
            </div>

            {/* Subtle hover effect indicator */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-50 opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none" />
        </div>
    );
}

export default PipelineCard;